import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { TextInput, TextInputRef } from 'shared/components/form/text-input';
import { Platform, Pressable, TextInputProps, View } from 'react-native';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from 'shared/components/dropdown-menu';
import { Text } from 'shared/components/text';
import markets from 'shared/@generated/markets.json';
import { useEffect, useMemo, useRef } from 'react';
import { SvgImage } from 'shared/components/svg-image';
import { LanguageCode } from 'shared/library/i18n/languages';
import { flags } from 'shared/library/i18n/flags';
import { Icon } from 'shared/components/icon';
import { runAfterInteractions } from 'shared/library/run-after-interactions';

const defaultMarket = markets[0]!;

const PhoneInput = ({
  value,
  onChange,
  ...props
}: Omit<TextInputProps, 'onChange' | 'value'> & {
  value: string;
  onChange: (value: string) => void;
}) => {
  const inputRef = useRef<TextInputRef>(null);
  const market = useMemo(() => {
    return markets.find((m) => value.startsWith(`+${m.calling_code}`)) ?? defaultMarket;
  }, [value]);
  const callingCode = market.calling_code.toString();
  const countryPrefix = `+${callingCode}`;
  const shouldDeferNativeAutoFocus = Platform.OS !== 'web' && props.autoFocus;
  const nativeContainerStyle =
    Platform.OS !== 'web'
      ? {
          borderColor: 'rgba(48, 25, 17, 0.15)',
          borderWidth: 1,
          borderRadius: 10,
          backgroundColor: '#ffffff',
          overflow: 'hidden' as const,
          ...(Platform.OS === 'ios' ? { borderCurve: 'continuous' as const } : {}),
        }
      : undefined;

  const getLocalPhoneNumber = (text: string) => {
    let clean = text.replace(/\D/g, '');
    const trimmed = text.trim();

    if (trimmed.startsWith('+') && clean.startsWith(callingCode)) {
      clean = clean.slice(callingCode.length);
    } else if (trimmed.startsWith('00') && clean.startsWith(`00${callingCode}`)) {
      clean = clean.slice(callingCode.length + 2);
    }

    if (clean.startsWith('0')) {
      clean = clean.slice(1);
    }

    return clean;
  };

  const handlePhoneChange = (text: string) => {
    const clean = getLocalPhoneNumber(text);
    onChange(`${countryPrefix}${clean}`);
  };

  useEffect(() => {
    if (!shouldDeferNativeAutoFocus) {
      return undefined;
    }

    return runAfterInteractions(() => {
      inputRef.current?.focus();
    });
  }, [shouldDeferNativeAutoFocus]);

  return (
    <View
      style={nativeContainerStyle}
      className={
        'border-brand-brown-dark/15 h-[54px] flex-row items-stretch overflow-hidden rounded-[10px] border bg-white'
      }
    >
      <View className={'flex-row items-center gap-1.5 pl-1.5'}>
        <DropdownMenuRoot>
          <DropdownMenuTrigger asChild>
            <Pressable className={'h-full py-1.5'} testID="phone-country-trigger">
              <View
                className={
                  'h-full flex-row items-center rounded bg-stone-100 px-3 py-2 hover:bg-stone-200'
                }
              >
                <SvgImage
                  src={flags[market.language_code as LanguageCode]}
                  containerClassName="mr-1 size-6"
                />

                <Icon icon={falAngleDown} className={'size-4 text-stone-600'} />
              </View>
            </Pressable>
          </DropdownMenuTrigger>

          <DropdownMenuContent align={'start'}>
            {markets.map((item) => (
              <DropdownMenuItem
                key={item.locale}
                testID={`phone-country-option-${item.calling_code}`}
                onSelect={() => {
                  const localNumber = value.startsWith(countryPrefix)
                    ? value.slice(countryPrefix.length)
                    : getLocalPhoneNumber(value);
                  onChange(`+${item.calling_code}${localNumber}`);
                  inputRef.current?.focus();
                }}
              >
                <SvgImage
                  src={flags[item.language_code as LanguageCode]}
                  containerClassName="mr-1 size-6"
                />
                <Text className={'flex-1 text-lg text-black'}>+{item.calling_code}</Text>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenuRoot>

        <Text testID={'phone-country-prefix'} className={'text-brand-brown-dark/50 text-lg'}>
          {countryPrefix}
        </Text>
      </View>

      <TextInput
        {...props}
        ref={inputRef}
        unstyled={true}
        autoFocus={shouldDeferNativeAutoFocus ? false : props.autoFocus}
        inputMode={'tel'}
        keyboardType={'phone-pad'}
        textContentType={'telephoneNumber'}
        importantForAutofill={'yes'}
        autoComplete={'tel'}
        placeholder={'XXXXXXXXX'}
        className={'h-full flex-1 px-4 text-lg'}
        value={
          value.startsWith(countryPrefix)
            ? value.slice(countryPrefix.length)
            : getLocalPhoneNumber(value)
        }
        onChangeText={handlePhoneChange}
        testID={props.testID ?? 'phone-input'}
      />
    </View>
  );
};

export { PhoneInput };
